import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api as cyranoApi } from "@/core/api/client";
import { poserLutilisateurCourant } from "@/core/demo/actif";

/**
 * Session-based auth pour le cockpit Dozka.
 *
 * Pattern : OAuth flow cote Cyrano FastAPI, cookie httpOnly opaque sur
 * dozka.app, tokens stockes serveur. Le browser ne touche jamais le JWT
 * WorkOS. Resout les bugs cookies tiers de l'AuthKit pure-SPA.
 *
 * API similaire a `useAuth` de authkit-react pour minimiser le diff cote
 * consommateurs (UserBadge, LoginScreen, UserSync, useAuthBridge).
 */
export interface SessionUser {
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePictureUrl: string | null;
  workosUserId: string;
  workosOrgId: string | null;
  roleSlug: string | null;
  isSuperadmin: boolean;
}

export interface SessionContextValue {
  /** Current user, ou null si pas authentifie. */
  user: SessionUser | null;
  /** True tant que le fetch initial /api/auth/me n'a pas resolu. */
  isLoading: boolean;
  /** Redirige le browser vers WorkOS authorize via /api/auth/login. */
  signIn: () => void;
  /** POST /api/auth/logout puis recharge la page (clear l'app state). */
  signOut: () => Promise<void>;
  /** Recupere un access_token JWT frais (pour Convex bridge). Lance si pas authent.
   *  `timeout` borne UNE tentative : le bridge Convex retente lui-meme, et
   *  prefere retenter vite qu'attendre les 20 s par defaut du client. */
  getAccessToken: (opts?: { timeout?: number }) => Promise<string>;
}

const SessionContext = createContext<SessionContextValue | null>(null);


interface ApiMeResponse {
  workos_user_id: string;
  workos_org_id: string | null;
  email: string;
  role_slug: string | null;
  is_superadmin: boolean;
  first_name: string | null;
  last_name: string | null;
  profile_picture_url: string | null;
}


function projectUser(api: ApiMeResponse): SessionUser {
  return {
    email: api.email,
    firstName: api.first_name,
    lastName: api.last_name,
    profilePictureUrl: api.profile_picture_url,
    workosUserId: api.workos_user_id,
    workosOrgId: api.workos_org_id,
    roleSlug: api.role_slug,
    isSuperadmin: api.is_superadmin,
  };
}


export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 🎬 Le mode démo est réservé à un compte de tournage, et il est consulté
  // depuis des couches sans React (le client HTTP, le flux SSE). On leur pose
  // donc l'identité ici, au moment MÊME où la session se résout — avant tout
  // rendu qui pourrait déclencher une requête. Cf `core/demo/actif.ts`.
  function poserLaSession(u: SessionUser | null) {
    poserLutilisateurCourant(u?.workosUserId ?? null);
    setUser(u);
  }

  // Bootstrap : fetch /api/auth/me au mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await cyranoApi.get<ApiMeResponse>("/api/auth/me");
        if (!cancelled) poserLaSession(projectUser(data));
      } catch (e) {
        // 401 = pas authentifie (normal). Autres erreurs : on log juste.
        if (!cancelled) poserLaSession(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(() => {
    // En Tauri, le on_navigation interceptor (lib.rs) re-route les URLs WorkOS
    // vers le browser systeme. Apres OAuth, le browser revient sur dozka.app
    // et il faut un hand-off via dozka:// pour que la webview Tauri (qui a son
    // propre cookie jar) recupere la session. C'est le role de
    // /auth/desktop-callback : le backend l'utilise comme signal pour emettre
    // un bootstrap token, le frontend (main.tsx) le hand-off via dozka://.
    //
    // En browser web pur, on revient SUR LA PAGE DEMANDÉE.
    //
    // 🪤 C'était "/" en dur jusqu'au 18-08-2026, et ça perdait tout visiteur
    // arrivant sans session sur une adresse gatée : lien partagé, fenêtre
    // privée, session expirée — il se connectait, WorkOS le ramenait à
    // l'accueil du cockpit, et il ne voyait JAMAIS ce qu'il était venu voir.
    // Aucune erreur, aucun message : juste la mauvaise page. Payé sur
    // `/regie-test-vente`, cherché deux fois du côté du cache PWA.
    //
    // Le backend re-valide de son côté (`auth.py` : chemin relatif, jamais
    // `//` — anti open redirect) ; on applique la même règle ici pour ne
    // jamais lui envoyer autre chose qu'un chemin de notre propre origine.
    const isInTauri = "__TAURI_INTERNALS__" in window;
    const chemin = window.location.pathname + window.location.search;
    const relatifSur = chemin.startsWith("/") && !chemin.startsWith("//");
    const returnTo = isInTauri
      ? "/auth/desktop-callback"
      : relatifSur
        ? chemin
        : "/";
    window.location.href = `/api/auth/login?return_to=${encodeURIComponent(returnTo)}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await cyranoApi.post("/api/auth/logout");
    } catch {
      // best-effort
    }
    // Reload pour clear tout l'app state (atoms, react-query cache, convex).
    window.location.href = "/";
  }, []);

  const getAccessToken = useCallback(
    async (opts?: { timeout?: number }): Promise<string> => {
      const data = await cyranoApi.get<{ access_token: string }>(
        "/api/auth/jwt",
        opts?.timeout === undefined ? undefined : { timeout: opts.timeout },
      );
      if (!data?.access_token) throw new Error("Not authenticated");
      return data.access_token;
    },
    [],
  );

  const value = useMemo<SessionContextValue>(
    () => ({ user, isLoading, signIn, signOut, getAccessToken }),
    [user, isLoading, signIn, signOut, getAccessToken],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}


export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return ctx;
}
